/**
 * @fileoverview Present reconciled Xero cash figures and retained manual matter values. The financials API owns ledger interpretation, readiness, and review flags; this panel polls its result and exposes source evidence without importing journals or updating accounting records.
 */
import React, { useEffect, useState } from 'react';
import api from '../../api';

const FIELDS = [
  ['gross_received', 'Gross received', 'Gross Received'],
  ['fee_revenue', 'Fees recovered', 'Fees Recovered'],
  ['costs_recovered', 'Costs recovered', 'Costs Recovered'],
  ['total_firm_receipts', 'Firm revenue', 'Revenue'],
  ['client_received', 'Client received', 'Client Received'],
  ['case_expenses', 'Case expenses', 'Case Expenses']
];
const currency = (value) => value == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value));

export default function XeroFinancials({ matterNumber, stage, substage }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!matterNumber) return undefined;
    let active = true;
    const load = async () => {
      try {
        const response = await api.get(`/case_details_xero_financials/${encodeURIComponent(matterNumber)}`);
        if (active) { setData(response.data); setError(false); }
      } catch { if (active) setError(true); }
    };
    setData(null);
    load();
    const timer = setInterval(load, 30000);
    return () => { active = false; clearInterval(timer); };
  }, [matterNumber, stage, substage]);
  if (!matterNumber) return null;
  const resolved = stage === 'Settled/Resolved' && substage === 'Payments Complete/Fully Resolved';
  const warning = error || data?.sync_error || data?.stale;
  const status = warning ? 'Sync needs attention' : !data?.sync_ready ? 'Initial sync pending' : data?.status === 'pending' ? (resolved ? 'Financial update pending' : 'Updates when payments are complete') : data.status === 'synced' ? 'Synced from Xero' : 'Review flagged amounts';
  return <div className="case-xero"><section className="xero-overview-panel" aria-label="Resolved case financials">
    <div className="xero-panel-header">
      <h2 className="xero-overview-title">Case financials</h2>
      <span role="status" className={`xero-pill xero-pill--${warning || data?.status === 'needs_review' ? 'warning' : 'neutral'}`}>{status}</span>
    </div>
    <div className="xero-stat-grid">
      {FIELDS.map(([key, label, attribute]) => {
        const state = data?.field_status?.[key];
        const manual = state === 'manual' || state === 'manual_conflict';
        const value = manual ? data?.manual_attributes?.[attribute] : data?.metrics?.[key];
        return <div className="xero-stat-card" key={key}>
          <div className="xero-stat-copy">
            <div className="xero-stat-label">{label}</div>
            <div className="xero-stat-value">{currency(value)}</div>
            <div className="xero-stat-meta">{manual ? 'Manual value retained' : state === 'synced' ? '✓ Xero' : 'Needs review'}{state === 'manual_conflict' && ` · Xero: ${currency(data?.metrics?.[key])}`}</div>
          </div>
        </div>;
      })}
    </div>
    <p className="xero-panel-description">Firm revenue includes fees and recovered costs. Amounts are in USD and use cash-basis journals.</p>
    {data?.last_sync_at && <p className="xero-stat-meta">Last complete sync: {new Date(data.last_sync_at + 'Z').toLocaleString()}</p>}
    {data?.issues?.length > 0 && <details><summary>Review {data.issues.length} financial checks</summary><ul>{data.issues.map(issue => <li key={issue}>{issue.replaceAll('_', ' ')}</li>)}</ul></details>}
    {data?.invoice_mapping_issues?.length > 0 && <details><summary>Invoice allocation needs review ({data.invoice_mapping_issues.length})</summary><ul>{data.invoice_mapping_issues.map(issue => <li key={issue.invoice_id}>{issue.invoice_id} · {issue.issue.replaceAll('_', ' ')}</li>)}</ul></details>}
    {data?.evidence?.length > 0 && <details><summary>Source journal entries ({data.evidence.length})</summary><ul>{data.evidence.map(line => <li key={line.JournalLineID}>Journal {line.JournalNumber || line.JournalID} · {line.AccountName} · {currency(line.NetAmount)}</li>)}</ul></details>}
  </section></div>;
}
